import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radar, Gift, Users, TrendingUp, ClipboardCheck, ArrowRight, Circle, AlertTriangle, CreditCard, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import DashboardShell from "@/components/hola/DashboardShell";
import RadarDiagnosis from "@/components/hola/RadarDiagnosis";
import UpgradeDialog from "@/components/hola/UpgradeDialog";
import { RaffleBanner } from "@/pages/UserTicketsPage";
import api from "@/lib/api";
import { toast } from "sonner";

export default function MerchantDashboard() {
  return (
    <DashboardShell active="home">
      {({ merchant, setMerchant }) => <DashboardBody merchant={merchant} setMerchant={setMerchant} />}
    </DashboardShell>
  );
}

function DashboardBody({ merchant, setMerchant }) {
  const [metrics, setMetrics] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchQuota, setMatchQuota] = useState(null);
  const [diagKey, setDiagKey] = useState(0);
  const [raffle, setRaffle] = useState(null);
  const [upgrade, setUpgrade] = useState(false);
  const isCupones = merchant.subscription?.tier === "cupones";
  useEffect(() => { api.get("/public/raffle").then((r) => setRaffle(r.data.current)).catch(() => {}); }, []);

  useEffect(() => {
    (async () => {
      try {
        const [m, mt, q] = await Promise.all([
          api.get("/merchants/me/metrics"),
          api.get("/merchants/me/matches"),
          api.get("/merchants/me/match-quota").catch(() => ({ data: null })),
        ]);
        setMetrics(m.data);
        setMatches(mt.data);
        setMatchQuota(q.data);
      } catch (e) { console.warn("silent error:", e && e.message || e); }
    })();
  }, []);

  const toggleRadar = async (val) => {
    if (isCupones) { setUpgrade(true); return; }
    try {
      const { data } = await api.put("/merchants/me", { radar_active: val });
      setMerchant(data);
      setDiagKey((k) => k + 1);
      toast.success(val ? "Radar del comercio ENCENDIDO" : "Radar APAGADO");
    } catch {
      toast.error("Error al cambiar radar");
    }
  };

  const hasLocation = merchant.lat && merchant.lng;
  const sub = merchant.subscription || {};
  const subActive = !!sub.active;
  const subExpiringSoon = subActive && (sub.days_remaining ?? 99) <= 3;

  return (
    <div className="space-y-6">
      {/* Subscription banner */}
      {(!subActive || subExpiringSoon) && (
        <div
          className={`rounded-2xl p-4 md:p-5 flex items-start md:items-center gap-3 flex-col md:flex-row ${
            !subActive
              ? "bg-red-50 border border-red-200"
              : "bg-amber-50 border border-amber-200"
          }`}
          data-testid="subscription-banner"
        >
          <div className={!subActive ? "text-red-500" : "text-amber-500"}>
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`font-bold ${!subActive ? "text-red-900" : "text-amber-900"}`}>
              {!subActive
                ? "Tu radar está pausado — la suscripción venció"
                : `Tu ${sub.is_trial ? "prueba" : "suscripción"} vence en ${sub.days_remaining} día${sub.days_remaining === 1 ? "" : "s"}`}
            </div>
            <p className={`text-sm mt-0.5 ${!subActive ? "text-red-800" : "text-amber-800"}`}>
              {!subActive
                ? "Los transeúntes ya no ven tus ofertas. Suscríbete para reactivar el radar al instante."
                : "Renueva ahora para no perder ni un match."}
            </p>
          </div>
          <Link to="/dashboard/billing">
            <Button size="sm" className={`rounded-full font-bold text-white ${!subActive ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}`} data-testid="banner-billing-cta">
              <CreditCard className="w-4 h-4" /> {!subActive ? "Reactivar" : "Renovar"}
            </Button>
          </Link>
        </div>
      )}

      {/* Top row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Panel de control</p>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">
            Hola, {merchant.business_name}
          </h1>
        </div>
        <div className={`flex items-center gap-3 rounded-2xl bg-white border border-neutral-200 px-4 py-3 ${isCupones ? "opacity-70" : ""}`} data-testid="radar-toggle-wrap">
          <div className="flex items-center gap-2">
            <Circle className={`w-3 h-3 fill-current ${merchant.radar_active && !isCupones ? "text-[#00FF7F]" : "text-neutral-300"}`} />
            <span className="text-sm font-bold">{isCupones ? "Radar GPS · plan completo" : merchant.radar_active ? "Radar activo" : "Radar apagado"}</span>
          </div>
          <Switch checked={!!merchant.radar_active && !isCupones} onCheckedChange={toggleRadar} data-testid="radar-switch" />
        </div>
      </div>
      <UpgradeDialog moduleName="Radar GPS" open={upgrade} onOpenChange={setUpgrade} />

      {/* Alert if no location */}
      {/* Autodiagnóstico del radar */}
      <RadarDiagnosis refreshKey={diagKey} />

      {raffle && (
        <Link to="/dashboard/sorteo" className="block" data-testid="dashboard-raffle-link">
          <RaffleBanner raffle={raffle} compact />
          <div className="text-xs text-neutral-500 mt-1 px-1">Programa de lealtad HOLA RADAR: entrega boletos a tus clientes por cada compra con tu QR de sorteo →</div>
        </Link>
      )}

      {!hasLocation && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 flex items-start gap-3" data-testid="no-location-alert">
          <div className="text-amber-500">
            <Radar className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-amber-900">Aún no has configurado tu ubicación</div>
            <p className="text-sm text-amber-800 mt-0.5">
              Sin ubicación GPS, tu comercio no aparecerá en el radar de los usuarios.
            </p>
          </div>
          <Link to="/dashboard/profile">
            <Button size="sm" className="rounded-full bg-amber-900 hover:bg-amber-800 text-white font-bold">
              Configurar
            </Button>
          </Link>
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard label="Matches" value={metrics?.total_matches ?? "—"} icon={Users} color="text-[#0055FF]" />
        <MetricCard label="Interesados" value={metrics?.interested ?? "—"} icon={TrendingUp} color="text-emerald-600" />
        <MetricCard label="Ofertas activas" value={metrics?.active_offers ?? "—"} icon={Gift} color="text-[#FF007A]" />
        <MetricCard label="Entregas" value={metrics?.redemptions ?? "—"} icon={ClipboardCheck} color="text-amber-500" />
        <MetricCard label="Conversión" value={metrics ? `${metrics.conversion_rate}%` : "—"} icon={Target} color="text-[#FF007A]" subtitle={metrics ? `${metrics.interest_rate}% interesados` : ""} />
      </div>

      {matchQuota && <MatchQuotaBar quota={matchQuota} />}

      {/* Recent matches */}
      <div className="rounded-2xl bg-white border border-neutral-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-extrabold">Matches recientes</h2>
          <Link to="/dashboard/offers">
            <Button variant="outline" size="sm" className="rounded-full">Gestionar ofertas <ArrowRight className="w-4 h-4" /></Button>
          </Link>
        </div>
        {matches.length === 0 ? (
          <div className="text-sm text-neutral-500 py-10 text-center" data-testid="no-matches">
            Todavía no hay matches. Asegúrate de tener ofertas activas y el radar encendido.
          </div>
        ) : (
          <div className="space-y-2">
            {matches.slice(0, 10).map((m) => (
              <div key={m.match_id} className="flex items-center justify-between p-3 rounded-xl bg-[#FAFAFA]">
                <div>
                  <div className="text-sm font-bold">Match a {Math.round(m.distance_m)}m</div>
                  <div className="text-xs text-neutral-500">{new Date(m.created_at).toLocaleString("es")}</div>
                </div>
                {m.interested ? (
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">Interesado</span>
                ) : (
                  <span className="text-xs font-bold text-neutral-500 bg-neutral-100 px-2 py-1 rounded-full">Notificado</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, color, subtitle }) {
  return (
    <div className="rounded-2xl bg-white border border-neutral-200 p-5">
      <Icon className={`w-5 h-5 ${color}`} />
      <div className="text-xs text-neutral-500 font-medium mt-3">{label}</div>
      <div className="font-heading font-extrabold text-3xl mt-1" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</div>
      {subtitle && <div className="text-[11px] text-neutral-400 mt-1">{subtitle}</div>}
    </div>
  );
}

function MatchQuotaBar({ quota }) {
  const used = quota.used_this_month || 0;
  const limit = quota.limit || 1;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const near = pct >= 80 && pct < 100;
  const full = pct >= 100;
  const barColor = full ? "bg-red-500" : near ? "bg-amber-500" : "bg-[#0055FF]";
  const planLabel = (quota.plan_id || "").includes("junior") ? "Junior" : "Pro";
  return (
    <div className="rounded-2xl bg-white border border-neutral-200 p-5" data-testid="match-quota-bar">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Cuota de matches este mes</div>
          <div className="font-heading font-extrabold text-lg mt-0.5">
            {used.toLocaleString()} <span className="text-neutral-400 font-normal text-base">/ {limit.toLocaleString()}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Plan</div>
          <div className="font-heading font-extrabold text-lg mt-0.5">{planLabel}</div>
        </div>
      </div>
      <div className="mt-3 h-2.5 rounded-full bg-neutral-100 overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 text-xs text-neutral-500 flex items-center justify-between">
        <span>{pct}% consumido</span>
        <span>{Math.max(0, limit - used).toLocaleString()} disponibles</span>
      </div>
      {near && !full && (
        <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
          Estás cerca de tu límite mensual. Considera cambiar a Pro para no perder matches al final del mes.
        </div>
      )}
      {full && (
        <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
          Alcanzaste el tope de tu plan este mes. No se generarán nuevos matches hasta el próximo ciclo o hasta que actualices tu plan.
        </div>
      )}
    </div>
  );
}

